import React, { useMemo, useState } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { AgreementStatus, SponsorAgreementResponseDto } from "@/lib/api/interfaces/sponsor-agreements.interface";
import { useSponsorAgreementAdvertisementsQuery, useSponsorAgreementDelete, useSponsorAgreementDocumentUrl } from "@/lib/api/requests/sponsor-agreements.requests";
import { Button } from "@/ui-components/ui/button";
import { PermissionFeatureWrapper } from "@/util-components/auth/PermissionFeatureWrapper";
import { Permission } from "@/lib/api/interfaces/permissions.interface";
import { PlanKey } from "@/lib/api/interfaces/billing.interface";
import { Loader2, Edit, Handshake, Calendar, Clock, Euro, FileText, MoreHorizontal, Trash2 } from "lucide-react";
import { CreateSponsorAgreementDialog } from "./CreateSponsorAgreementDialog";
import { useOrganizationsCustomerFindOneQuery } from "@/lib/api/requests/organizations.requests";
import { cn } from "@/lib/utils/cn";
import { StandardDeleteDialog } from "@/util-components/dialog/StandardDeleteDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/util-components/dropdown-menu";

interface SponsorAgreementsSectionProps {
  sponsorId: string;
  query: UseQueryResult<SponsorAgreementResponseDto[]>;
  statusFilter: AgreementStatus;
  setStatusFilter: (status: AgreementStatus) => void;
}

export const SponsorAgreementsSection: React.FC<SponsorAgreementsSectionProps> = ({
  sponsorId,
  query,
  statusFilter,
  setStatusFilter,
}) => {
  const { data, isLoading, isError } = query;
  const { data: organization } = useOrganizationsCustomerFindOneQuery();
  const enableMandantFeature = organization?.enableMandantFeature ?? false;

  // Normalisiere mögliche Response-Formate zu einem Array von Agreements
  const agreements: SponsorAgreementResponseDto[] = (() => {
    if (!data) return [];
    if (Array.isArray(data)) return data as SponsorAgreementResponseDto[];
    const anyData = data as any;
    if (Array.isArray(anyData.docs)) return anyData.docs as SponsorAgreementResponseDto[];
    if (Array.isArray(anyData.items)) return anyData.items as SponsorAgreementResponseDto[];
    if (Array.isArray(anyData.data)) return anyData.data as SponsorAgreementResponseDto[];
    return [];
  })();

  const [openDialog, setOpenDialog] = useState(false);
  const [editingAgreement, setEditingAgreement] = useState<SponsorAgreementResponseDto | undefined>(undefined);
  const [deletingAgreementId, setDeletingAgreementId] = useState<string | null>(null);

  const handleEdit = (agreement: SponsorAgreementResponseDto) => {
    setEditingAgreement(agreement);
    setOpenDialog(true);
  };

  // Advertisements (Leistungen) laden, um Titel zu den IDs zu resolven
  const { data: advertisements } = useSponsorAgreementAdvertisementsQuery(sponsorId);
  const advertisementNameById = useMemo(() => {
    const map = new Map<string, string>();
    (advertisements || []).forEach((ad) => {
      map.set(ad.advertisementId, ad.advertisementName);
    });
    return map;
  }, [advertisements]);

  const docUrlMutation = useSponsorAgreementDocumentUrl(sponsorId);
  const deleteMutation = useSponsorAgreementDelete(sponsorId);

  const handleDelete = (agreementId: string) => {
    setDeletingAgreementId(agreementId);
  };

  const confirmDelete = async () => {
    if (!deletingAgreementId) return;
    try {
      await deleteMutation.mutateAsync({ agreementId: deletingAgreementId });
      setDeletingAgreementId(null);
    } catch (error) {
      console.error("Failed to delete agreement", error);
    }
  };

  const filteredAgreements: SponsorAgreementResponseDto[] = agreements.filter((a) => a.status === statusFilter);
  const [showAll, setShowAll] = useState(false);
  const visibleAgreements = showAll ? filteredAgreements : filteredAgreements.slice(0, 3);
  const hasExtraAgreements = filteredAgreements.length > 3;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-1 rounded-xl bg-gray-50 p-1 border border-gray-100">
          <button
            type="button"
            onClick={() => {
              setShowAll(false);
              setStatusFilter(AgreementStatus.PENDING);
            }}
            className={cn(
              "px-3 py-1.5 rounded-lg text-xs font-medium transition-all",
              statusFilter === AgreementStatus.PENDING
                ? "bg-white text-blue-600 shadow-sm border border-gray-100"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-100/50"
            )}
          >
            Ausstehend
          </button>
          <button
            type="button"
            onClick={() => {
              setShowAll(false);
              setStatusFilter(AgreementStatus.ACCEPTED);
            }}
            className={cn(
              "px-3 py-1.5 rounded-lg text-xs font-medium transition-all",
              statusFilter === AgreementStatus.ACCEPTED
                ? "bg-white text-emerald-600 shadow-sm border border-gray-100"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-100/50"
            )}
          >
            Akzeptiert
          </button>
          <button
            type="button"
            onClick={() => {
              setShowAll(false);
              setStatusFilter(AgreementStatus.REJECTED);
            }}
            className={cn(
              "px-3 py-1.5 rounded-lg text-xs font-medium transition-all",
              statusFilter === AgreementStatus.REJECTED
                ? "bg-white text-rose-600 shadow-sm border border-gray-100"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-100/50"
            )}
          >
            Abgelehnt
          </button>
        </div>
      </div>

      {/* Liste / Status */}
      {isLoading ? (
        <div className="flex h-32 items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50/50">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            <p className="text-xs text-gray-500 font-medium">Vereinbarungen werden geladen...</p>
          </div>
        </div>
      ) : isError ? (
        <div className="rounded-2xl bg-rose-50 p-4 border border-rose-100 text-sm text-rose-700 flex items-center gap-3">
          <div className="h-2 w-2 rounded-full bg-rose-500 animate-pulse" />
          Vereinbarungen konnten nicht geladen werden.
        </div>
      ) : filteredAgreements.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 px-4 rounded-2xl border border-dashed border-gray-200 bg-gray-50/50 text-center">
          <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center mb-3">
            <Handshake className="h-5 w-5 text-gray-400" />
          </div>
          <p className="text-sm font-medium text-gray-900">Keine Vereinbarungen gefunden</p>
          <p className="text-xs text-gray-500 mt-1 max-w-[200px]">
            {statusFilter === AgreementStatus.PENDING 
              ? "Es liegen momentan keine ausstehenden Vereinbarungen vor."
              : statusFilter === AgreementStatus.ACCEPTED
              ? "Es wurden noch keine Vereinbarungen akzeptiert."
              : "Es wurden bisher keine Vereinbarungen abgelehnt."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid gap-3">
            {visibleAgreements.map((agreement) => {
            const ids = agreement.advertisementIds || [];
            const names = ids.map((id) => advertisementNameById.get(id) || "Leistung");
            const displayName = names.length > 0 ? names.join(", ") : "Leistung";

            return (
              <div 
                key={agreement._id} 
                className="group relative min-w-0 overflow-hidden rounded-xl border border-gray-100 bg-white p-4 transition-colors hover:border-gray-200"
              >
                <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                  <div className="absolute right-2 top-2 z-10">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          data-card-actions-trigger="true"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-lg p-0 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                          onClick={(e) => e.stopPropagation()}
                          onPointerDown={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(agreement);
                          }}
                          className="cursor-pointer"
                        >
                          <Edit className="mr-2 h-4 w-4" />
                          Bearbeiten
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(agreement._id);
                          }}
                          className="cursor-pointer text-red-600 focus:bg-red-50 focus:text-red-600"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Löschen
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </PermissionFeatureWrapper>

                <div className="min-w-0">
                  <div className="min-w-0">
                    <div className="mb-1 min-w-0 pr-9">
                      <h3 
                        className="text-sm font-bold text-gray-900 truncate" 
                        title={displayName}
                      >
                        {displayName}
                      </h3>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        {agreement.advertisementType && (
                          <span className={cn(
                            "inline-flex items-center rounded-full border bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shrink-0",
                            agreement.advertisementType === "DONATION" 
                              ? "border-gray-900 text-gray-900"
                              : "border-gray-300 text-gray-700"
                          )}>
                            {agreement.advertisementType === "DONATION" ? "Spende" : "Sponsoring"}
                          </span>
                        )}
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border shrink-0",
                            agreement.status === "accepted"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                              : agreement.status === "rejected"
                              ? "bg-rose-50 text-rose-700 border-rose-100"
                              : "bg-blue-50 text-blue-700 border-blue-100"
                          )}
                        >
                          {agreement.status === "accepted"
                            ? "Akzeptiert"
                            : agreement.status === "rejected"
                            ? "Abgelehnt"
                            : "Ausstehend"}
                        </span>
                        {enableMandantFeature && agreement.mandantType && (
                          <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-700 border border-indigo-100 uppercase tracking-wider shrink-0">
                            {agreement.mandantType}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
                      {(agreement.startDate || agreement.endDate) && (
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          <span>
                            {agreement.startDate && new Date(agreement.startDate).toLocaleDateString('de-DE')}
                            {agreement.startDate && agreement.endDate && " — "}
                            {agreement.endDate && new Date(agreement.endDate).toLocaleDateString('de-DE')}
                          </span>
                        </div>
                      )}
                      {agreement.duration !== undefined && (
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          <span>{agreement.duration} Tage</span>
                        </div>
                      )}
                      {agreement.volume !== undefined && (
                        <div className="flex items-center gap-1 font-semibold text-gray-700">
                          <Euro className="h-3 w-3" />
                          <span>{new Intl.NumberFormat("de-DE").format(agreement.volume)}€</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {agreement.documents && agreement.documents.length > 0 && (
                  <div className="pt-3 border-t border-gray-50">
                    <div className="flex flex-wrap gap-2">
                      {agreement.documents.map((doc, idx) => (
                        <button
                          key={`${agreement._id}-${idx}`}
                          type="button"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const res = await docUrlMutation.mutateAsync({ agreementId: agreement._id, docIndex: idx });
                              if (res?.url) {
                                window.open(res.url, "_blank", "noopener,noreferrer");
                              }
                            } catch {}
                          }}
                          disabled={docUrlMutation.isPending}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-100 bg-gray-50/50 px-2 py-1 text-[11px] font-medium text-gray-600 hover:bg-white hover:border-blue-200 hover:text-blue-600 transition-all disabled:opacity-50"
                        >
                          <FileText className="h-3 w-3" />
                          <span className="truncate max-w-[120px]">{doc.name || `Dokument ${idx + 1}`}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          </div>
          {hasExtraAgreements && (
            <div className="pt-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAll((prev) => !prev)}
                className="w-full text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-all rounded-xl"
              >
                {showAll
                  ? "Weniger Vereinbarungen anzeigen"
                  : `Weitere ${filteredAgreements.length - visibleAgreements.length} Vereinbarungen anzeigen`}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Dialog zum Anlegen oder Bearbeiten eines Agreements */}
      <CreateSponsorAgreementDialog
        open={openDialog}
        onOpenChange={(open) => {
          setOpenDialog(open);
          if (!open) setEditingAgreement(undefined);
        }}
        sponsorId={sponsorId}
        agreement={editingAgreement}
        onCreated={() => {
          query.refetch();
          setOpenDialog(false);
        }}
      />

      <StandardDeleteDialog
        open={!!deletingAgreementId}
        onOpenChange={(open) => !open && setDeletingAgreementId(null)}
        title="Vereinbarung löschen"
        description="Möchten Sie diese Vereinbarung wirklich unwiderruflich löschen?"
        itemTitle={(() => {
          const agreement = agreements.find(a => a._id === deletingAgreementId);
          if (!agreement) return "";
          const ids = agreement.advertisementIds || [];
          const names = ids.map((id) => advertisementNameById.get(id) || "Leistung");
          return names.length > 0 ? names.join(", ") : "Leistung";
        })()}
        onDelete={confirmDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};
